import React, { useState, useEffect, useRef } from "react";
import { FaTimes, FaPaperPlane } from "react-icons/fa";
import axios from "axios";
import { io } from "socket.io-client";


// --- Default starter messages if no chat history
const starterMessages = [
  {
    id: "starter-1",
    sender_id: 0,
    message: "Hi there! 👋 Start your conversation by introducing yourself.",
    created_at: new Date().toISOString(),
    read_status: true,
  },
  {
    id: "starter-2",
    sender_id: 0,
    message: "Tip: Ask about hobbies or interests to break the ice!",
    created_at: new Date().toISOString(),
    read_status: true,
  },
  {
    id: "starter-3",
    sender_id: 0,
    message: "You can say something like: 'Hey! I noticed we have similar interests.'",
    created_at: new Date().toISOString(),
    read_status: true,
  },
];



function ChatDrawer({ uid, show, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [myUserId, setMyUserId] = useState(null);

  const token = localStorage.getItem("token");
  const API_BASE = "https://chat.soulsathi.com";

  const socketRef = useRef();
  const messagesEndRef = useRef();

 
  // --- Fetch my userId from backend
  const fetchMyUserId = async () => {
    try {
      const res = await axios.get(`${API_BASE}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMyUserId(res.data.id);
    } catch (err) {
      console.error("Failed to fetch user info:", err);
    }
  };

  // --- Format time and day
  const formatTime = (dateStr) =>
    new Date(dateStr).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  const getDayLabel = (dateStr) => {
    const msgDate = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (
      msgDate.getDate() === today.getDate() &&
      msgDate.getMonth() === today.getMonth() &&
      msgDate.getFullYear() === today.getFullYear()
    )
      return "Today";
    if (
      msgDate.getDate() === yesterday.getDate() &&
      msgDate.getMonth() === yesterday.getMonth() &&
      msgDate.getFullYear() === yesterday.getFullYear()
    )
      return "Yesterday";

    return msgDate.toLocaleDateString();
  };

  const groupMessagesByDay = () => {
    const grouped = {};
    messages.forEach((msg) => {
      const day = getDayLabel(msg.created_at);
      if (!grouped[day]) grouped[day] = [];
      grouped[day].push(msg);
    });
    return grouped;
  };

  // --- Fetch chat history
  const fetchChatHistory = async () => {
    if (!token || !uid) return;
    try {
      const res = await axios.post(`${API_BASE}/chat/history`, { token, uid });
      setMessages(res.data);
      scrollToBottom();
    } catch (err) {
      console.error("Failed to fetch chat history:", err);
    }
  };

  // --- Socket.IO
  useEffect(() => {
    if (!token) return;

    socketRef.current = io(API_BASE);
    socketRef.current.emit("join", { token });

    socketRef.current.on("new_message", (msg) => {
      setMessages((prev) => {
        if (prev.some((m) => m.id === msg.id)) return prev;
        return [...prev, msg];
      });
      scrollToBottom();
    });

    return () => socketRef.current.disconnect();
  }, [token]);

  useEffect(() => {
    fetchMyUserId();
  }, []);

  useEffect(() => {
    if (show) fetchChatHistory();
  }, [show, uid]);


  useEffect(() => {
  if (show && myUserId) {
    const fetchAndMarkRead = async () => {
      await fetchChatHistory();
      scrollToBottom();
      // mark messages as read
      try {
        await axios.post(`${API_BASE}/chat/read`, { token, uid });
      } catch (err) {
        console.error("Failed to mark messages as read:", err);
      }
    };
    fetchAndMarkRead();
  }
}, [show, uid, myUserId]);


  // --- Send message
  const sendMessage = async () => {
    if (!input.trim() || !token) return;
    const msgContent = input;
    setInput("");
    try {
      await axios.post(`${API_BASE}/chat/send`, { token, uid, message: msgContent });
      fetchChatHistory(); // fetch updated messages
    } catch (err) {
      console.error("Failed to send message:", err);
    }
  };

  const groupedMessages = groupMessagesByDay();



  useEffect(() => {
  if (show) {
    const fetchAndScroll = async () => {
      await fetchChatHistory();
      scrollToBottom(); // scroll after messages load
    };
    fetchAndScroll();
  }
}, [show, uid]);



const scrollToBottom = () => {
  setTimeout(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, 50); // small delay ensures DOM has updated
};

  return (
    <>
      {show && <div className="chat-overlay" onClick={onClose}></div>}

      <div className={`chat-drawer ${show ? "open" : ""}`}>
        <div className="chat-header d-flex justify-content-between align-items-center p-2">
          <strong>Chat</strong>
          <button className="btn btn-sm" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className="chat-body p-2">
  {messages.length === 0 && (
    <>
      {starterMessages.map((msg) => (
        <div key={msg.id} className="chat-msg other">
          <span>{msg.message}</span>
          <div className="chat-time">{formatTime(msg.created_at)}</div>
        </div>
      ))}
      <div className="text-muted text-center mt-2">Start chatting...</div>
    </>
  )}

  {messages.length > 0 &&
    Object.entries(groupMessagesByDay()).map(([day, msgs], i) => (
      <div key={i}>
        <div className="chat-day-label text-center my-1">{day}</div>
        <div className="flex flex-col">
          {msgs.map((msg) => {
            const sender = msg.sender_id === myUserId ? "me" : "other";
            return (
              <div key={msg.id} className={`chat-msg ${sender}`}>
                <span>{msg.message}</span>
                <div className="chat-time">
                  {formatTime(msg.created_at)}{" "}
                  {sender === "me" && msg.read_status && "✔✔"}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    ))}
  <div ref={messagesEndRef} />
</div>

        <div className="chat-footer d-flex p-2">
          <input
            type="text"
            className="form-control me-2"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type a message..."
            onKeyDown={(e) => e.key === "Enter" && sendMessage()}
          />
          <button className="btn btn-primary" onClick={sendMessage}>
            <FaPaperPlane />
          </button>
        </div>
      </div>
    </>
  );
}

export default ChatDrawer;
